import React, { useRef } from 'react';
import BoardSelect from './BoardSelect.jsx';
import { FiHash, FiCopy, FiTrash2, FiDownload, FiUpload, FiEdit2, FiFolderPlus, FiMoon, FiSun, FiMaximize, FiHelpCircle, FiGrid, FiPlus, FiX } from 'react-icons/fi';

const AppHeader = function({ready, onAdd, onNewBoard, creating}) {
  return <header className='appHead h'>
      <div className='brand h'>
        <img src='/icon.svg' alt='' width='34' height='34' />
        <h1>stickii<span>.</span></h1>
        <span className='tagline'>a space for your thoughts</span>
      </div>
      <div className='headActions h'>
        <button
          className='newBoardButton h'
          aria-label='new board'
          onClick={onNewBoard}
          disabled={!ready || creating}
        >
          <FiFolderPlus size={18} aria-hidden='true' /><span>new board</span>
        </button>
        <button className='newNoteButton h' onClick={onAdd} disabled={!ready}>
          <FiPlus size={19} aria-hidden='true' />
          <span>new note (n)</span>
        </button>
      </div>
    </header>;
};

const BoardToolbar = function({
  name,
  onRename,
  ready,
  boards,
  boardId,
  onBoardChange,
  onExport,
  onImport,
  importing,
  onDeleteBoard,
  onDuplicateBoard
}) {
  const nameRef = useRef(null);
  const importRef = useRef(null);

  return <div className='boardBar h'>
      <div className='boardTitle h'>
        <input
          ref={nameRef}
          className='boardName'
          aria-label='board name'
          value={name}
          disabled={!ready}
          placeholder='my board'
          onChange={event => onRename(event.target.value)}
          onBlur={() => {
            if (!name.trim()) onRename('my board');
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
        <button
          className='boardRenameButton'
          aria-label='rename board'
          title='rename board'
          disabled={!ready}
          onClick={() => {
            nameRef.current.focus();
            nameRef.current.select();
          }}
        ><FiEdit2 size={13} aria-hidden='true' /></button>
      </div>
      <div className='boardToolbarActions h'>
        <button
          className='boardRenameButton'
          aria-label='export board'
          title='export board'
          disabled={!ready}
          onClick={onExport}
        ><FiDownload size={15} aria-hidden='true' /></button>
        <button
          className='boardRenameButton'
          aria-label='import boards'
          title='import boards'
          disabled={importing || !boards.length}
          onClick={() => importRef.current.click()}
        ><FiUpload size={15} aria-hidden='true' /></button>
        <input
          ref={importRef}
          className='srOnly'
          type='file'
          accept='.json,application/json'
          tabIndex={-1}
          aria-label='choose board export'
          onChange={event => {
            const file = event.target.files[0];

            // Allow selecting the same file again after an import attempt.
            event.target.value = '';
            onImport(file);
          }}
        />
        <button
          className='boardRenameButton'
          aria-label='duplicate board'
          title='duplicate board'
          disabled={!ready || importing || !boards.length}
          onClick={onDuplicateBoard}
        ><FiCopy size={15} aria-hidden='true' /></button>
        {boards.length > 1 && <BoardSelect boards={boards} boardId={boardId} onChange={onBoardChange} />}
        <button
          className='boardRenameButton boardDeleteButton'
          aria-label='delete board'
          title='delete board'
          disabled={!ready || importing || !boards.length}
          onClick={onDeleteBoard}
        ><FiTrash2 size={15} aria-hidden='true' /></button>
      </div>
    </div>;
};

const EmptyBoard = function({onAdd}) {
  return <div className='boardEmpty v'>
      <img src='/icon.svg' alt='' width='64' height='64' />
      <h2>Make room for an idea.</h2>
      <p>Add a note (N) or paste something worth keeping.</p>
    </div>;
};

const BoardFooter = function({
  calculatorOpen,
  onCalculatorToggle,
  saveStatus,
  zoom,
  onZoomChange,
  snapToGrid,
  onSnapToggle,
  onFit,
  hasItems,
  onHelp,
  theme,
  onThemeToggle
}) {
  return <footer className='appFoot h'>
      <span>
        Drag to arrange <span className='separator'>·</span> Pull a corner to resize
        {' '}<span className='separator'>·</span> Paste text & images
      </span>
      <a className='creatorCredit' href='https://mercurii.tech' target='_blank' rel='noopener noreferrer'>created by mercurii.tech</a>
      <div className='footerActions h'>
      <span
        className={`saveStatus ${saveStatus === 'Not saved' ? 'hasError' : ''}`}
        role='status'
      >
        <span className='statusDot' /> {saveStatus}
      </span>
      <button className='snapToggle' aria-label='calculator' title='toggle calculator (c)' aria-pressed={calculatorOpen} onClick={onCalculatorToggle}><FiHash size={15} aria-hidden='true' /></button>
      <button
        className='snapToggle'
        aria-label='fit all items'
        title='fit all items (f)'
        disabled={!hasItems}
        onClick={onFit}
      ><FiMaximize size={15} aria-hidden='true' /></button>
      <button
        className='snapToggle'
        aria-label='snap to grid'
        aria-pressed={snapToGrid}
        title={`snap to grid: ${snapToGrid ? 'on' : 'off'}`}
        onClick={onSnapToggle}
      ><FiGrid size={15} aria-hidden='true' /></button>
      <div className='zoomControls h' role='group' aria-label='board zoom'>
        <button
          aria-label='zoom out'
          title='zoom out'
          disabled={zoom <= 0.05}
          onClick={() => onZoomChange(zoom - 0.25)}
        >−</button>
        <button
          className='zoomValue'
          aria-label={`zoom ${Math.round(zoom * 100)} percent; reset to 100 percent`}
          title='reset zoom'
          onClick={() => onZoomChange(1)}
        >{Math.round(zoom * 100)}%</button>
        <button
          aria-label='zoom in'
          title='zoom in'
          disabled={zoom >= 3}
          onClick={() => onZoomChange(zoom + 0.25)}
        >+</button>
      </div>
      <button
        className='snapToggle'
        aria-label='dark theme'
        aria-pressed={theme === 'dark'}
        title={`switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        onClick={onThemeToggle}
      >{theme === 'dark' ? <FiSun size={15} aria-hidden='true' /> : <FiMoon size={15} aria-hidden='true' />}</button>
      <button
        className='snapToggle'
        aria-label='keyboard help'
        title='keyboard help (?)'
        onClick={onHelp}
      ><FiHelpCircle size={15} aria-hidden='true' /></button>
      </div>
    </footer>;
};

const BoardToast = function({message, canUndo, onUndo, onDismiss}) {
  if (!message && !canUndo) return null;

  return <div className='toast h' role='status'>
      <span>{message || 'Note deleted.'}</span>
      {!message && canUndo && <button onClick={onUndo}>Undo</button>}
      <button className='iconButton' aria-label='Dismiss message' onClick={onDismiss}>
        <FiX size={18} aria-hidden='true' />
      </button>
    </div>;
};

export {
  AppHeader,
  BoardToolbar,
  EmptyBoard,
  BoardFooter,
  BoardToast
};
